import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDateTime, formatDateTimeForList } from '../../../config/appConfig';
import type { HealthLevel, SystemHealthSnapshot } from '../types';
import { formatDuration, healthLabel } from '../utils';

interface SyncLogTimelineProps {
  logs: SystemHealthSnapshot['emailSync']['recentLogs'];
  limit?: number;
  variant?: 'timeline' | 'compact';
}

function logLevel(status: string): HealthLevel {
  switch (status) {
    case 'completed':
      return 'ok';
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'started':
      return 'info';
    default:
      return 'paused';
  }
}

const levelStyles: Record<
  HealthLevel,
  { dot: string; dotFill: string; line: string; card: string; badge: string; progress: string }
> = {
  ok: {
    dot: 'bg-emerald-500 ring-emerald-500/20',
    dotFill: 'bg-emerald-500',
    line: 'bg-emerald-500/25',
    card: 'border-emerald-200/70 bg-emerald-50/40 dark:border-emerald-900/50 dark:bg-emerald-950/20',
    badge: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300',
    progress: 'bg-emerald-500',
  },
  warning: {
    dot: 'bg-amber-500 ring-amber-500/20',
    dotFill: 'bg-amber-500',
    line: 'bg-amber-500/25',
    card: 'border-amber-200/70 bg-amber-50/40 dark:border-amber-900/50 dark:bg-amber-950/20',
    badge: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300',
    progress: 'bg-amber-500',
  },
  error: {
    dot: 'bg-rose-500 ring-rose-500/20',
    dotFill: 'bg-rose-500',
    line: 'bg-rose-500/25',
    card: 'border-rose-200/70 bg-rose-50/40 dark:border-rose-900/50 dark:bg-rose-950/20',
    badge: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300',
    progress: 'bg-rose-500',
  },
  info: {
    dot: 'bg-sky-500 ring-sky-500/20 animate-pulse',
    dotFill: 'bg-sky-500 animate-pulse',
    line: 'bg-sky-500/25',
    card: 'border-sky-200/70 bg-sky-50/40 dark:border-sky-900/50 dark:bg-sky-950/20',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-300',
    progress: 'bg-sky-500',
  },
  paused: {
    dot: 'bg-slate-400 ring-slate-400/20',
    dotFill: 'bg-slate-400',
    line: 'bg-slate-400/25',
    card: 'border-slate-200/80 bg-slate-50/50 dark:border-slate-700/50 dark:bg-slate-900/30',
    badge: 'bg-slate-100 text-slate-700 dark:bg-slate-700/40 dark:text-slate-300',
    progress: 'bg-slate-400',
  },
};

const EmptyState: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { colors } = useTheme();

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'py-8 px-4' : 'rounded-2xl border border-dashed py-14 px-6',
        !compact && colors.border,
        !compact && colors.surfaceHover,
      )}
    >
      <div
        className={cn(
          'mb-3 flex h-10 w-10 items-center justify-center rounded-full',
          colors.surface,
          colors.border,
          'border',
        )}
      >
        <svg className={cn('h-5 w-5', colors.textMuted)} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      </div>
      <p className={cn('text-sm font-medium', colors.text)}>No sync activity yet</p>
      <p className={cn('text-xs mt-1 max-w-xs', colors.textMuted)}>
        Completed sync runs will appear here after accounts sync.
      </p>
    </div>
  );
};

const SyncLogTimeline: React.FC<SyncLogTimelineProps> = ({ logs, limit, variant = 'timeline' }) => {
  const { colors } = useTheme();
  const visibleLogs = limit != null ? logs.slice(0, limit) : logs;

  if (visibleLogs.length === 0) {
    return <EmptyState compact={variant === 'compact'} />;
  }

  if (variant === 'compact') {
    return (
      <div className="space-y-2">
        {visibleLogs.map((log) => {
          const level = logLevel(log.status);
          const style = levelStyles[level];
          const summary = log.errorMessage || log.message;
          const whenLabel = formatDateTimeForList(log.createdAt);
          const whenTitle = formatDateTime(log.createdAt);
          const durationLabel = log.duration != null ? formatDuration(Math.round(log.duration / 1000)) : null;
          const result =
            log.emailsFound > 0
              ? `${log.emailsSaved}/${log.emailsFound} saved${durationLabel ? ` · ${durationLabel}` : ''}`
              : durationLabel ?? '—';

          return (
            <div
              key={log.id}
              className={cn(
                'rounded-xl border px-3 py-2.5',
                style.card,
              )}
            >
              <div className="flex items-start gap-2.5 min-w-0">
                <div className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', style.dotFill)} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className={cn('text-sm font-medium truncate', colors.text)}>{log.emailAddress}</p>
                    <time className={cn('text-[11px] tabular-nums shrink-0', colors.textMuted)} title={whenTitle}>
                      {whenLabel}
                    </time>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold', style.badge)}>
                      {healthLabel(level)}
                    </span>
                    <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>{result}</span>
                  </div>
                  {summary && (
                    <p className={cn('text-xs mt-1 line-clamp-2', colors.textMuted)} title={summary}>
                      {summary}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="relative pl-1">
      {visibleLogs.map((log, index) => {
        const level = logLevel(log.status);
        const style = levelStyles[level];
        const isLast = index === visibleLogs.length - 1;
        const summary = log.errorMessage || log.message;
        const savePercent =
          log.emailsFound > 0 ? Math.min(100, Math.round((log.emailsSaved / log.emailsFound) * 100)) : 0;
        const durationLabel = log.duration != null ? formatDuration(Math.round(log.duration / 1000)) : null;
        const whenLabel = formatDateTimeForList(log.createdAt);
        const whenTitle = formatDateTime(log.createdAt);

        return (
          <div key={log.id} className="relative flex gap-4 pb-4 last:pb-0">
            <div className="flex w-5 flex-col items-center shrink-0 pt-5">
              <div className={cn('h-3 w-3 rounded-full ring-4 shrink-0', style.dot, colors.background)} />
              {!isLast && <div className={cn('mt-1 w-0.5 flex-1 min-h-[20px] rounded-full', style.line)} />}
            </div>

            <article
              className={cn(
                'min-w-0 flex-1 rounded-xl border px-4 py-3.5 transition-shadow hover:shadow-sm',
                style.card,
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-2 gap-y-1">
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-semibold truncate', colors.text)}>{log.emailAddress}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold', style.badge)}>
                    {healthLabel(level)}
                  </span>
                  <time className={cn('text-xs tabular-nums whitespace-nowrap', colors.textMuted)} title={whenTitle}>
                    {whenLabel}
                  </time>
                </div>
              </div>

              {log.emailsFound > 0 && (
                <div className="mt-3">
                  <div className="flex items-center justify-between gap-2 text-[11px]">
                    <span className={cn('font-medium', colors.textSecondary)}>
                      {log.emailsSaved.toLocaleString()} of {log.emailsFound.toLocaleString()} emails saved
                    </span>
                    <span className={cn('tabular-nums', colors.textMuted)}>{savePercent}%</span>
                  </div>
                  <div
                    className={cn(
                      'mt-1.5 h-1.5 w-full overflow-hidden rounded-full',
                      'bg-black/5 dark:bg-white/10',
                    )}
                  >
                    <div
                      className={cn('h-full rounded-full transition-all', style.progress)}
                      style={{ width: `${savePercent}%` }}
                    />
                  </div>
                </div>
              )}

              <div className={cn('mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs', colors.textMuted)}>
                {durationLabel && (
                  <span className="inline-flex items-center gap-1">
                    <svg className="h-3.5 w-3.5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {durationLabel}
                  </span>
                )}
                {log.emailsFailed > 0 && (
                  <span className="text-rose-600 dark:text-rose-400 font-medium">
                    {log.emailsFailed} failed
                  </span>
                )}
              </div>

              {summary && (
                <p className={cn('mt-2 text-sm leading-snug break-words', colors.textMuted)} title={summary}>
                  {summary}
                </p>
              )}
            </article>
          </div>
        );
      })}
    </div>
  );
};

export default SyncLogTimeline;
